/**
 * Brand Record — HANDOFF §3, "a tenant is a record, not a stylesheet".
 *
 * RESPONSIBILITY: the shape of everything a tenant supplies. Pure types plus
 * the neutral default. No Angular, no DOM, no I/O — `brand-record-tokens.ts`
 * turns a record into custom properties and `BrandConfigService` writes them.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHAT THE PLATFORM CAN STORE TODAY
 *
 * A record is NOT the division document. `Customer.divisions[]` (owned by
 * rdx-platform-api) carries exactly seven brand fields:
 *
 *   logo · logoDark · logoLight · primaryColor · secondaryColor
 *   textOnPrimaryColor · banner
 *
 * Every field below is marked with where it comes from:
 *
 *   [stored]  — backed by a real column on the division document today.
 *   [derived] — computed from a stored field; nothing to add.
 *   [GAP]     — §3 asks for it, the division document has nowhere to put it.
 *               These resolve from the neutral default at runtime and can
 *               only be exercised by a fixture until the schema grows. Each
 *               one is a concrete field request, not a nice-to-have.
 *
 * The gaps are the finding. Colour was never the hard part — §3's claim is
 * that three tenants should read as three products, and the levers that do
 * that work (action separated from ink, a type pairing, a weight map, an
 * icon stroke, a shape scale, a voice) have no home in the model. A tenant
 * whose record is `{ primaryColor }` can only ever be a recolour.
 *
 * AI CONTEXT: never branch on a tenant id. Every difference between two
 * tenants must be expressible as a difference between two records; if it
 * cannot be, that is a missing field on this interface, not an `if`.
 */

import type { PaperKind } from '../utils/brand-color';

// ── Depth ───────────────────────────────────────────────────

/**
 * §3.2 — "customers adopt gradually; both must look deliberate."
 *
 *   accent — ink on actions, focus and active states. Paper, shape, type and
 *            voice stay Ricoh Paper. This is what the app does today and it
 *            stays the safe default for a cautious customer.
 *   full   — the record also drives tint, shape, type, icon stroke and voice.
 */
export type BrandDepth = 'accent' | 'full';

// ── Colour ──────────────────────────────────────────────────

/**
 * §3.1 — the load-bearing rule. The old model welded action to accent, which
 * makes a bottle-green identity with a near-black primary button
 * inexpressible. `source` records the tenant's INTENT so the resolved colour
 * can be re-derived per paper rather than frozen at one hex:
 *
 *   accent  — the action is the ink. Fused on purpose.
 *   neutral — the action is the paper's own ink; the identity colour stays
 *             free for tints and active states.
 *   custom  — the action is its own value.
 */
export type BrandActionSource = 'accent' | 'neutral' | 'custom';

export interface BrandAction {
  source: BrandActionSource;
  /** Required when `source` is `custom`. Ignored otherwise. */
  value?: string;
}

// ── Type ────────────────────────────────────────────────────

/**
 * §3.4 — "a brand face may not ship the weights the UI assumes."
 *
 * The UI must read `var(--fw-semibold)`, never a literal `600`. A face with
 * no 600 either synthesises one or silently snaps to 400, and the whole
 * hierarchy flattens with no error anywhere.
 */
export interface BrandWeightMap {
  medium: number;
  semibold: number;
  bold: number;
}

export interface BrandType {
  /** Editorial face — mastheads, greetings, display sizes. */
  display: string;
  /** Interface face — everything else. */
  ui: string;
  weights: BrandWeightMap;
  /** §3.2 `caseRule` — easyJet sets display copy lowercase. */
  caseDisplay: 'none' | 'lowercase' | 'uppercase';
  /** Display tracking. A tight lowercase wordmark is not a light serif. */
  trackDisplay: string;
}

// ── Shape ───────────────────────────────────────────────────

export interface BrandShape {
  /** Card / panel radius, px. */
  container: number;
  /** Control radius, px. */
  control: number;
  /** Badge and pill radius, px. A 4px badge reads very differently to 100px. */
  badge: number;
  /** Hairline weight, px. */
  border: number;
  /** Avatar corner — `50%` for a circle, a px value for a squircle. */
  avatar: string;
  /** Icon stroke width. 1.5 reads financial, 2.5 reads consumer. */
  iconStroke: number;
}

// ── Voice ───────────────────────────────────────────────────

/**
 * §3.5 — "a bank says colleagues; an airline says the team."
 *
 * `register` picks between translated variants of the same string, so a
 * tenant changes voice without anyone re-writing (or re-translating) copy.
 * `terms` is a per-tenant noun map and is deliberately NOT translated: it is
 * the customer's own vocabulary, supplied by the customer.
 */
export type BrandRegister = 'formal' | 'plain' | 'warm' | 'brisk';

export interface BrandVoice {
  register: BrandRegister;
  /** Trailing punctuation on a greeting. Some brands set none. */
  punctuation: string;
  /** Noun substitutions, e.g. `{ building: 'site' }`. */
  terms: Readonly<Record<string, string>>;
}

// ── Mark ────────────────────────────────────────────────────

/**
 * §3.2 `mark` / `markAsset`.
 *
 * ⚠️ The asset is a URL the customer supplies, never a file in this repo.
 * HANDOFF §1.7 and open question 7 both say so: third-party trademarks
 * cannot be vendored here. `letters` is the fallback when no asset is set,
 * and it is also what an unlicensed face degrades to.
 */
export type BrandMarkKind = 'svg' | 'letters';

export interface BrandMark {
  kind: BrandMarkKind;
  /** [stored → division.logo / logoDark / logoLight] */
  asset: string | null;
  assetOnDark: string | null;
  assetOnLight: string | null;
}

// ── The record ──────────────────────────────────────────────

export interface BrandRecord {
  /** [stored → division.name] */
  name: string;
  /** [GAP] Breadcrumb / rail form. Falls back to the first word of `name`. */
  shortName: string;

  /** [stored → division.logo*] */
  mark: BrandMark;

  /** [stored → division.primaryColor] §3.1 identity colour. */
  ink: string;
  /** [GAP] §3.1 primary-action colour. Needs `action.source` + `action.value`. */
  action: BrandAction;
  /**
   * [GAP] §1.7 — how much of the brand hue the paper takes, in percent.
   * Capped hard on write; see `brand-record-tokens.ts` for why.
   */
  tint: number;

  /** [GAP] §3.4. Needs three columns: display face, ui face, weight map. */
  type: BrandType;
  /** [GAP] §3.2 shape scale. Needs six columns. */
  shape: BrandShape;
  /** [GAP] §3.5. Needs a register enum and a free-form term map. */
  voice: BrandVoice;

  /**
   * [GAP] §3.2 `markerGlyph` — the brand's list marker. Trivial on its own;
   * listed because §3 counts it, and because a record with nowhere to put it
   * is the same class of problem as the face.
   */
  markerGlyph: string;

  /**
   * [GAP] §3.2 / §3.6 — graphic language and imagery. NOT implemented, and
   * deliberately so: `motif`, `pattern`, `banner`, `bannerFocus` and the
   * named `imagerySlots[]` all need asset storage with a focus point and a
   * scrim guarantee per slot. `division.banner` is a single URL with no
   * focus point and no slot name, so it can express exactly one of them.
   * Modelled here so the shortfall is visible in one place rather than
   * discovered per surface.
   */
  imagery: {
    /** [stored → division.banner] The one slot the schema can express. */
    banner: string | null;
    /** [GAP] Focus point for that banner, `x% y%`. */
    bannerFocus: string | null;
    /** [GAP] Named slots — services header, community cover, empty states. */
    slots: Readonly<Record<string, string>>;
    /** [GAP] Motif and repeating device. */
    motif: string | null;
    pattern: string | null;
  };
}

/** A record plus the depth it is applied at. */
export interface BrandConfig {
  record: BrandRecord;
  depth: BrandDepth;
}

// ── Neutral default ─────────────────────────────────────────

/**
 * Ricoh Paper with no tenant expression: the values every `[GAP]` field
 * resolves to until the schema can carry the tenant's own. Deliberately the
 * SAME numbers the stylesheet already ships, so applying the default record
 * is a no-op — that is what makes `accent` depth honest rather than a second
 * code path.
 */
export const NEUTRAL_BRAND_TYPE: BrandType = {
  display: 'var(--font-sans)',
  ui: 'var(--font-sans)',
  weights: { medium: 500, semibold: 600, bold: 700 },
  caseDisplay: 'none',
  trackDisplay: '-0.028em',
};

export const NEUTRAL_BRAND_SHAPE: BrandShape = {
  container: 16,
  control: 12,
  badge: 100,
  border: 1,
  avatar: '50%',
  iconStroke: 2,
};

export const NEUTRAL_BRAND_VOICE: BrandVoice = {
  register: 'warm',
  punctuation: '.',
  terms: {},
};

/** Build a record from the fields the division document can actually store. */
export function brandRecordFromDivision(input: {
  name: string;
  shortName?: string | null;
  primaryColor: string;
  logo?: string | null;
  logoDark?: string | null;
  logoLight?: string | null;
  banner?: string | null;
}): BrandRecord {
  const asset = input.logo ?? null;
  return {
    name: input.name,
    shortName: input.shortName?.trim() || input.name.split(' ')[0],
    mark: {
      kind: asset || input.logoDark || input.logoLight ? 'svg' : 'letters',
      asset,
      assetOnDark: input.logoDark ?? null,
      assetOnLight: input.logoLight ?? null,
    },
    ink: input.primaryColor,
    // Today's behaviour, stated rather than assumed: with no stored action
    // the action IS the accent. That is the fusion §3.1 wants named.
    action: { source: 'accent' },
    tint: 0,
    type: NEUTRAL_BRAND_TYPE,
    shape: NEUTRAL_BRAND_SHAPE,
    voice: NEUTRAL_BRAND_VOICE,
    markerGlyph: '—',
    imagery: {
      banner: input.banner ?? null,
      bannerFocus: null,
      slots: {},
      motif: null,
      pattern: null,
    },
  };
}

/**
 * Which paper a mark asset is printed on.
 *
 * Re-exported from the colour utility so a caller resolving an asset does not
 * have to reach into two modules. The distinction matters because an asset is
 * chosen in TypeScript and therefore does NOT follow a re-papered subtree the
 * way a token does — see `DivisionBrandingService.logoOn()`.
 */
export type { PaperKind };
